import { createContext } from 'react';
import { IUpdateCustomerBPA, BpaPaymentSystem, CardBrands } from 'types';

export type TTypeOfModalWindow = null | 'toConfirmFieldsClearing' | 'toConfirmSubmit' | 'success' | 'error';

export interface IBpaCardBrand {
	id: string;
	bpaPaymentSystem: Nullable<BpaPaymentSystem>;
	cardBrand: Nullable<CardBrands>;
	bankFee: Nullable<number>;
	kmFee: Nullable<number>;
}

export interface IBpaTariff {
	id: string;
	typeOfBpaPaymentSystem: Nullable<BpaPaymentSystem>;
	data: IBpaCardBrand[];
}

export interface IBpaSet {
	id: string;
	isOpenSet: boolean;
	name: Nullable<string>;
	recipientAccountNumber: Nullable<string>;
	recipientName: Nullable<string>;
	recipientBank: Nullable<string>;
	recipientBankCity: Nullable<string>;
	recipientInn: Nullable<string>;
	recipientBic: Nullable<string>;
	recipientCorrespondentAccount: Nullable<string>;
	paymentDetails: Nullable<string>;
	cardContractNumber: Nullable<string>;
	sbpContractNumber: Nullable<string>;
	tariffs: IBpaTariff[];
}

export interface IBpaState {
	needRefetchData: boolean;
	bpa: IBpaSet[];
	dataForSubmit: Nullable<IUpdateCustomerBPA>;
	typeOfModalWindow: TTypeOfModalWindow;
}

export const bpaInitialState: IBpaState = {
	needRefetchData: false,
	bpa: [],
	dataForSubmit: null,
	typeOfModalWindow: null,
};

export interface IBpaContext extends IBpaState {
	refetchData(value: boolean): void;
	addBpaBlock(): void;
	deleteBpaBlock(setId: string): void;
	toggleIsOpenBpaBlock(setId: string): void;
	changeName({ setId, value }: { setId: string; value: string }): void;
	changeRecipientAccountNumber({ setId, value }: { setId: string; value: string }): void;
	changeRecipientName({ setId, value }: { setId: string; value: string }): void;
	changeRecipientBank({ setId, value }: { setId: string; value: string }): void;
	changeRecipientBankCity({ setId, value }: { setId: string; value: string }): void;
	changeRecipientInn({ setId, value }: { setId: string; value: string }): void;
	changeRecipientBic({ setId, value }: { setId: string; value: string }): void;
	changeRecipientCorrespondentAccount({ setId, value }: { setId: string; value: string }): void;
	changePaymentDetails({ setId, value }: { setId: string; value: string }): void;
	changeCardContractNumber({ setId, value }: { setId: string; value: string }): void;
	changeSbpContractNumber({ setId, value }: { setId: string; value: string }): void;
	addTariff(setId: string): void;
	deleteTariff({ setId, tariffId }: { setId: string; tariffId: string }): void;
	addCardBrand({ setId, tariffId }: { setId: string; tariffId: string }): void;
	deleteCardBrand({ setId, tariffId, brandId }: { setId: string; tariffId: string; brandId: string }): void;
	changeBpaPaymentSystem({
		setId,
		tariffId,
		value,
	}: {
		setId: string;
		tariffId: string;
		value: BpaPaymentSystem;
	}): void;
	changeCardBrand({
		setId,
		tariffId,
		brandId,
		value,
	}: {
		setId: string;
		tariffId: string;
		brandId: string;
		value: CardBrands;
	}): void;
	changeBankFee({
		setId,
		tariffId,
		brandId,
		value,
	}: {
		setId: string;
		tariffId: string;
		brandId: string;
		value: Nullable<number>;
	}): void;
	changeKMFee({
		setId,
		tariffId,
		brandId,
		value,
	}: {
		setId: string;
		tariffId: string;
		brandId: string;
		value: Nullable<number>;
	}): void;
	setDataForSubmit(value: Nullable<IUpdateCustomerBPA>): void;
	changeTypeOfModalWindow(value: TTypeOfModalWindow): void;
}

export const BpaContext = createContext<IBpaContext>({
	refetchData: () => undefined,
	addBpaBlock: () => undefined,
	deleteBpaBlock: () => undefined,
	toggleIsOpenBpaBlock: () => undefined,
	changeName: () => undefined,
	changeRecipientAccountNumber: () => undefined,
	changeRecipientName: () => undefined,
	changeRecipientBank: () => undefined,
	changeRecipientBankCity: () => undefined,
	changeRecipientInn: () => undefined,
	changeRecipientBic: () => undefined,
	changeRecipientCorrespondentAccount: () => undefined,
	changePaymentDetails: () => undefined,
	changeCardContractNumber: () => undefined,
	changeSbpContractNumber: () => undefined,
	addTariff: () => undefined,
	deleteTariff: () => undefined,
	addCardBrand: () => undefined,
	deleteCardBrand: () => undefined,
	changeBpaPaymentSystem: () => undefined,
	changeCardBrand: () => undefined,
	changeBankFee: () => undefined,
	changeKMFee: () => undefined,
	setDataForSubmit: () => undefined,
	changeTypeOfModalWindow: () => undefined,
	...bpaInitialState,
});
